import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import toast from 'react-hot-toast';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Code2,
  AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon,
  Undo2, Redo2, Minus, Highlighter,
  Sparkles, X, Loader2, RotateCcw,
} from 'lucide-react';
import './RichTextEditor.css';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const API = process.env.REACT_APP_API_URL || '';

// ── Toolbar button ──────────────────────────────────────────────────────────
function Btn({ onClick, active, disabled, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded-lg transition-colors ${
        active
          ? 'bg-indigo-100 text-indigo-700'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" />;
}

// ── AI Panel ────────────────────────────────────────────────────────────────
function AIPanel({ onClose, onInsert }) {
  const [topic, setTopic] = useState('');
  const [context, setContext] = useState('');
  const [type, setType] = useState('blog');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState('');
  const token = localStorage.getItem('token');

  async function generate() {
    if (!topic.trim()) { toast.error('Enter a topic first'); return; }
    setGenerating(true);
    setPreview('');
    try {
      const res = await fetch(`${API}/api/marketing/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic: topic.trim(), context: context.trim(), type, tone, length }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Generation failed');
      setPreview(data.html);
    } catch (e) {
      toast.error(e.message || 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  }

  const selectCls = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 bg-white';

  return (
    <div className="border-t border-slate-200 bg-gradient-to-br from-indigo-50/80 to-purple-50/60">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}>
              <Sparkles size={15} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">Write with AI</p>
              <p className="text-[11px] text-slate-400">NDIS-aware writing, ready for you to edit</p>
            </div>
          </div>
          <button onMouseDown={e => { e.preventDefault(); onClose(); }}
            className="p-1.5 rounded-lg hover:bg-white/80 text-slate-400 hover:text-slate-600">
            <X size={15} />
          </button>
        </div>

        {/* Options row */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Type</label>
            <select value={type} onChange={e => setType(e.target.value)} className={selectCls}>
              <option value="blog">Blog Post</option>
              <option value="page">Web Page</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Tone</label>
            <select value={tone} onChange={e => setTone(e.target.value)} className={selectCls}>
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="educational">Educational</option>
              <option value="persuasive">Persuasive</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Length</label>
            <select value={length} onChange={e => setLength(e.target.value)} className={selectCls}>
              <option value="short">Short (300–500w)</option>
              <option value="medium">Medium (700–1000w)</option>
              <option value="long">Long (1200–1800w)</option>
            </select>
          </div>
        </div>

        {/* Topic */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Topic / Prompt *</label>
          <textarea
            value={topic}
            onChange={e => setTopic(e.target.value)}
            rows={2}
            placeholder="e.g. How NDIS providers can improve roster efficiency using technology"
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 bg-white resize-none"
          />
        </div>

        {/* Optional context */}
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
            Context <span className="font-normal normal-case text-slate-400">(optional)</span>
          </label>
          <input
            type="text"
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="e.g. Target: registered managers. Include PACE billing. Focus on compliance."
            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 bg-white"
          />
        </div>

        {/* Generate */}
        <button
          type="button"
          onClick={generate}
          disabled={generating || !topic.trim()}
          className="flex items-center justify-center gap-2 w-full py-3 text-sm font-bold text-white rounded-xl transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
        >
          {generating ? (
            <><Loader2 size={15} className="animate-spin" /> Writing…</>
          ) : (
            <><Sparkles size={15} /> Generate Content</>
          )}
        </button>

        {/* Preview */}
        {preview && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Preview</p>
              <button type="button" onClick={generate} disabled={generating}
                className="flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-slate-600 px-2 py-1 rounded-lg hover:bg-white/80">
                <RotateCcw size={10} /> Regenerate
              </button>
            </div>
            <div
              className="bg-white border border-slate-200 rounded-xl p-4 max-h-52 overflow-y-auto text-sm text-slate-700 leading-relaxed prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview) }}
            />
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => onInsert(preview, 'replace')}
                className="py-2.5 text-sm font-bold text-white rounded-xl transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #1e2a78, #4f46e5)' }}>
                Replace content
              </button>
              <button type="button" onClick={() => onInsert(preview, 'append')}
                className="py-2.5 text-sm font-bold text-indigo-700 rounded-xl border border-indigo-200 bg-white hover:bg-indigo-50 transition-colors">
                Append to editor
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main RichTextEditor ─────────────────────────────────────────────────────
export default function RichTextEditor({ value = '', onChange, placeholder = 'Start writing...', minHeight = 320 }) {
  const [aiOpen, setAiOpen] = useState(false);
  const lastValueRef = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: false }),
      Placeholder.configure({ placeholder }),
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastValueRef.current = html;
      onChange?.(html);
    },
  });

  // Sync when value changes externally (e.g. async load of existing post)
  useEffect(() => {
    if (!editor) return;
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL:', prev);
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  function handleInsert(html, mode) {
    if (!editor) return;
    if (mode === 'replace') {
      const newHTML = html;
      lastValueRef.current = newHTML;
      editor.commands.setContent(newHTML, false);
      onChange?.(newHTML);
    } else {
      editor.commands.insertContentAt(editor.state.doc.content.size, html);
      const newHTML = editor.getHTML();
      lastValueRef.current = newHTML;
      onChange?.(newHTML);
    }
    setAiOpen(false);
    toast.success('Content inserted');
  }

  const wordCount = editor
    ? editor.getText().split(/\s+/).filter(w => w.length > 0).length
    : 0;

  if (!editor) return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center" style={{ minHeight }}>
      <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="tiptap-wrapper rounded-2xl border border-slate-200 overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="tiptap-toolbar flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-slate-200 bg-slate-50/80">
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)"><Undo2 size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Ctrl+Shift+Z)"><Redo2 size={15} /></Btn>
        <Sep />

        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold (Ctrl+B)"><Bold size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic (Ctrl+I)"><Italic size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline (Ctrl+U)"><UnderlineIcon size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><Strikethrough size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Highlight"><Highlighter size={15} /></Btn>
        <Sep />

        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3"><Heading3 size={15} /></Btn>
        <Sep />

        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list"><List size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list"><ListOrdered size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote"><Quote size={15} /></Btn>
        <Sep />

        <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align left"><AlignLeft size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align center"><AlignCenter size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align right"><AlignRight size={15} /></Btn>
        <Sep />

        <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Inline code"><Code size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code block"><Code2 size={15} /></Btn>
        <Btn onClick={setLink} active={editor.isActive('link')} title="Add / edit link"><LinkIcon size={15} /></Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule"><Minus size={15} /></Btn>
        <Sep />

        {/* AI writing button */}
        <button
          type="button"
          onMouseDown={e => { e.preventDefault(); setAiOpen(o => !o); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ml-auto ${
            aiOpen
              ? 'text-white shadow-md'
              : 'text-white hover:shadow-md hover:opacity-90'
          }`}
          style={{ background: aiOpen ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : 'linear-gradient(135deg, #4f46e5, #7c3aed)' }}
        >
          <Sparkles size={13} />
          Write with AI
        </button>
      </div>

      {/* AI Panel */}
      {aiOpen && <AIPanel onClose={() => setAiOpen(false)} onInsert={handleInsert} />}

      {/* Editor area */}
      <div className="tiptap-editor" style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>

      {/* Status bar */}
      <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
        <span className="text-[11px] text-slate-400">{wordCount} words</span>
        <span className="text-[11px] text-slate-300">Ctrl+B bold · Ctrl+I italic · Ctrl+Z undo</span>
      </div>
    </div>
  );
}
